import { memo, useCallback } from 'react'

import { ControlPointDuplicate, RotateLeft } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Skeleton, Typography, Divider } from '@mui/material'
import { SmepEvent } from '@nativewaves/nw-cloud-sdk/smep'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useEventCreateMutation, useEventDeleteMutation, useEventWakeMutation } from 'hooks/mutations/smep'

const RowActionsRaw = ({ row, onClose }: RowActionsProps<SmepEvent>) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createSmepEvent, ...smepEventCreateMutation } = useEventCreateMutation({ originId: row.original.id })
  const { mutate: deleteSmepEvent } = useEventDeleteMutation()
  const { mutate: reprocessEvent, ...eventReprocessMutation } = useEventWakeMutation()

  const handleDuplicateSmepEvent = useCallback(() => {
    createSmepEvent(
      { smepEventInsert: { ...row.original, name: `${row.original.name} (${t('duplicate')})` } },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }, [createSmepEvent, onClose, row.original, t])

  const handleDeleteSmepEvent = useCallback(() => {
    onClose()

    setTimeout(() => {
      deleteSmepEvent(
        { eventId: row.original.id },
        {
          onSuccess: () => {
            enqueueSnackbar(t('domain:Workspace.Event.eventDeleted', { count: 1 }), { variant: 'success' })
          },
          onError: () => {
            enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
          },
        },
      )
    }, 300)
  }, [deleteSmepEvent, enqueueSnackbar, onClose, row.original.id, t])

  const handleReprocessEvent = useCallback(() => {
    reprocessEvent(
      { eventId: row.original.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Workspace.Event.eventReprocessed', { count: 1 }), { variant: 'success' })
          onClose()
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [enqueueSnackbar, onClose, reprocessEvent, row.original.id, t])

  return (
    <>
      <MenuItem onClick={handleReprocessEvent} disabled={eventReprocessMutation.isPending}>
        <ListItemIcon>
          <RotateLeft />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {eventReprocessMutation.isPending ? <Skeleton variant="text" /> : t('domain:Workspace.Event.reprocess')}
        </Typography>
      </MenuItem>
      <MenuItem onClick={handleDuplicateSmepEvent} disabled={smepEventCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {smepEventCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeleteSmepEvent} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
